import { computed, ComputedRef, inject, provide, ref, Ref } from "vue";
import {
  AdTemplatePreroll,
  CampaignAdTemplatePreroll,
  BackgroundImageSetMap
} from "@fresh8/types/src";
import useBackgroundImage from "./useBackgroundImage";

export function usePreroll(
  brandPreroll: ComputedRef<AdTemplatePreroll | undefined>,
  sizeId: ComputedRef<string | undefined>,
  prerollImageSet: ComputedRef<BackgroundImageSetMap | undefined>,
  campaignPreroll?: ComputedRef<CampaignAdTemplatePreroll | undefined>
): {
  prerollTargetUrl: ComputedRef<string | undefined>;
  usePreroll: ComputedRef<boolean>;
  prerollImage: ComputedRef<string | undefined>;
  prerollName: ComputedRef<string | undefined>;
  prerollDuration: ComputedRef<number | undefined>;
  prerollClicked: Ref<boolean>;
  isCampaignPreroll: Ref<boolean>;
} {
  const prerollImageData = useBackgroundImage(
    sizeId as ComputedRef<string>,
    "preroll",
    prerollImageSet as ComputedRef<BackgroundImageSetMap>
  );

  const preroll = computed(() => {
    if (campaignPreroll?.value && campaignPreroll?.value.usePreroll) {
      return {
        prerollTargetUrl: campaignPreroll.value.prerollTargetUrl,
        prerollDuration: campaignPreroll.value.prerollDuration,
        usePreroll: campaignPreroll.value.usePreroll,
        campaignPreroll: true
      };
    }
    return {
      prerollTargetUrl: brandPreroll?.value?.prerollTargetUrl,
      prerollDuration: brandPreroll?.value?.prerollDuration,
      usePreroll: brandPreroll?.value?.usePreroll,
      campaignPreroll: false
    };
  });

  const usePreroll = computed(() => {
    return (
      !!preroll.value.usePreroll && !!prerollImageData.value.backgroundImage
    );
  });

  const prerollClicked = ref(false);

  return {
    prerollTargetUrl: computed(() => preroll?.value?.prerollTargetUrl),
    usePreroll,
    prerollImage: computed(() => prerollImageData.value.backgroundImage),
    prerollName: computed(() => prerollImageData.value.backgroundImageSetName),
    prerollDuration: computed(() => preroll?.value?.prerollDuration ?? 3),
    prerollClicked,
    isCampaignPreroll: computed(() => preroll.value.campaignPreroll)
  };
}

export type PrerollContext = ReturnType<typeof usePreroll>;
export const brandSymbol = Symbol("prerollSymbol");

export function providePreroll(
  brandPreroll: ComputedRef<AdTemplatePreroll | undefined>,
  sizeId: ComputedRef<string | undefined>,
  prerollImageSet: ComputedRef<BackgroundImageSetMap | undefined>,
  campaignPreroll?: ComputedRef<CampaignAdTemplatePreroll | undefined>
): void {
  provide(
    brandSymbol,
    usePreroll(brandPreroll, sizeId, prerollImageSet, campaignPreroll)
  );
}

export function injectPreroll(): PrerollContext | undefined {
  return inject<PrerollContext>(brandSymbol);
}
